import { useEffect, useState, useMemo, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  RefreshCw,
  Search,
  X,
  AlertTriangle,
  ExternalLink,
  Edit3,
  Trash2,
  Map as MapIcon,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot,
} from 'recharts';
import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import { api } from '../api/client.js';
import { useSession } from '../auth/SessionContext.jsx';

/* ---------- helpers ---------- */

function timeAgo(ms) {
  if (!ms) return 'jamais';
  const diff = Date.now() - ms;
  if (diff < 60_000) return "à l'instant";
  if (diff < 3600_000) return `il y a ${Math.floor(diff / 60000)} min`;
  if (diff < 86400_000) return `il y a ${Math.floor(diff / 3600000)} h`;
  return `il y a ${Math.floor(diff / 86400000)} j`;
}

const STATUT_LABEL = {
  normal: 'Normal',
  attention: 'Attention',
  critique: 'Critique',
  inconnu: 'Inconnu',
};

const STATUT_COLOR = {
  normal: '#2EB6A7',
  attention: '#f97316',
  critique: '#c0392b',
  inconnu: '#a8b4c9',
};

const STATUT_BG = {
  normal: '#e6f7f4',
  attention: '#fff1e6',
  critique: '#fdecea',
  inconnu: '#eef0f4',
};

const REFRESH_MS = 30_000;

function buildSensorsQuery({ statut, secteur, q }) {
  const parts = [];
  if (statut) parts.push(`statut=${encodeURIComponent(statut)}`);
  if (secteur) parts.push(`secteur=${encodeURIComponent(secteur)}`);
  if (q) parts.push(`q=${encodeURIComponent(q)}`);
  return parts.length ? '?' + parts.join('&') : '';
}

function fmtTime(ms) {
  if (!ms) return '';
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

/* ---------- main page ---------- */

export default function AdminGestion() {
  const { session } = useSession();
  const nav = useNavigate();

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [secteurs, setSecteurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({ q: '', statut: '', secteur: '' });
  const [searchInput, setSearchInput] = useState('');
  const debounceRef = useRef(null);

  const [refreshTick, setRefreshTick] = useState(0); // pulse indicator
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);

  if (session && session.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  /* fetch sensors + stats */
  const fetchAll = async (filtersToUse = filters, silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [list, st] = await Promise.all([
        api.get(`/api/admin/sensors${buildSensorsQuery(filtersToUse)}`),
        api.get('/api/admin/sensors/stats'),
      ]);
      setItems(Array.isArray(list?.items) ? list.items : []);
      setStats(st || null);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      setError(e.message || 'Erreur de chargement des capteurs');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  /* fetch secteurs once */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/api/admin/sensors/secteurs');
        if (!cancelled) setSecteurs(Array.isArray(r?.secteurs) ? r.secteurs : []);
      } catch {
        if (!cancelled) setSecteurs([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* initial load + refetch on filters */
  useEffect(() => {
    fetchAll(filters);
  }, [filters.statut, filters.secteur, filters.q]);

  /* auto-refresh every 30s (silent) */
  useEffect(() => {
    const id = setInterval(() => {
      fetchAll(filters, true);
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [filters.statut, filters.secteur, filters.q]);

  /* debounce search input -> filters.q */
  const onSearchChange = (val) => {
    setSearchInput(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setFilters((f) => ({ ...f, q: val.trim() }));
    }, 300);
  };

  const onResetFilters = () => {
    setSearchInput('');
    setFilters({ q: '', statut: '', secteur: '' });
  };

  const onForceRefresh = () => fetchAll(filters, false);

  const selectedItem = useMemo(
    () => items.find((it) => it.device_id === selectedDeviceId) || null,
    [items, selectedDeviceId]
  );

  const total = stats?.total ?? items.length;
  const pct = (n) => (total > 0 ? Math.round(((n || 0) / total) * 100) : 0);

  return (
    <div className="gestion-page">
      <Header />

      <main className="gestion-main">
        {/* Page header */}
        <div className="gestion-header">
          <div className="gestion-header-left">
            <h1 className="gestion-title">Gestion des capteurs</h1>
            <p className="gestion-subtitle">
              Vertuoso · {total} capteur{total > 1 ? 's' : ''} · auto-refresh 30s
            </p>
          </div>
          <div className="gestion-header-right">
            <button
              type="button"
              className="vrt-btn vrt-btn--ghost"
              onClick={() => nav('/admin/gestion/carte')}
              style={{ minHeight: 40, padding: '0 14px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              title="Voir les capteurs sur la carte"
            >
              <MapIcon size={16} /> Voir sur la carte
            </button>
            <button type="button" className="gestion-btn-refresh" onClick={onForceRefresh} title="Forcer le rafraîchissement">
              <RefreshCw size={16} />
              Actualiser
            </button>
            <span
              key={refreshTick}
              className="gestion-pulse-dot"
              aria-label="Auto-refresh actif"
              title="Auto-refresh actif"
            />
          </div>
        </div>

        {error && (
          <div className="gestion-error" role="alert">
            Erreur : {error}
          </div>
        )}

        {/* KPI band */}
        <div className="gestion-kpis">
          <div className="gestion-kpi">
            <div className="gestion-kpi-value">{stats?.total ?? 0}</div>
            <div className="gestion-kpi-label">Total</div>
          </div>
          <div className="gestion-kpi-separator" />
          <div className="gestion-kpi">
            <div className="gestion-kpi-value" style={{ color: STATUT_COLOR.normal }}>
              {stats?.normal ?? 0}
            </div>
            <div className="gestion-kpi-label">Normaux</div>
            <div className="gestion-kpi-pct">({pct(stats?.normal)}%)</div>
          </div>
          <div className="gestion-kpi-separator" />
          <div className="gestion-kpi">
            <div className="gestion-kpi-value" style={{ color: STATUT_COLOR.attention }}>
              {stats?.attention ?? 0}
            </div>
            <div className="gestion-kpi-label">Attention</div>
            <div className="gestion-kpi-pct">({pct(stats?.attention)}%)</div>
          </div>
          <div className="gestion-kpi-separator" />
          <div className="gestion-kpi">
            <div className="gestion-kpi-value" style={{ color: STATUT_COLOR.critique }}>
              {stats?.critique ?? 0}
            </div>
            <div className="gestion-kpi-label">Critiques</div>
            <div className="gestion-kpi-pct">({pct(stats?.critique)}%)</div>
          </div>
          <div className="gestion-kpi-separator" />
          <div className="gestion-kpi">
            <div className="gestion-kpi-value" style={{ color: STATUT_COLOR.critique }}>
              {stats?.eau_detectee ?? 0}
            </div>
            <div className="gestion-kpi-label">Eau détectée</div>
          </div>
        </div>

        {/* Filters */}
        <div className="gestion-filters">
          <div className="gestion-search">
            <Search size={16} />
            <input
              type="text"
              className="gestion-search-input"
              placeholder="Rechercher un device_id…"
              value={searchInput}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>

          <select
            className="gestion-filter-input"
            value={filters.secteur}
            onChange={(e) => setFilters((f) => ({ ...f, secteur: e.target.value }))}
            aria-label="Filtrer par secteur"
          >
            <option value="">Tous les secteurs</option>
            {secteurs.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          <div className="gestion-chips">
            {[
              { v: '', label: 'Tous', color: 'var(--vrt-navy)' },
              { v: 'normal', label: 'Normal', color: STATUT_COLOR.normal },
              { v: 'attention', label: 'Attention', color: STATUT_COLOR.attention },
              { v: 'critique', label: 'Critique', color: STATUT_COLOR.critique },
            ].map((c) => {
              const active = filters.statut === c.v;
              return (
                <button
                  key={c.v || 'all'}
                  type="button"
                  className="gestion-chip"
                  onClick={() => setFilters((f) => ({ ...f, statut: c.v }))}
                  style={{
                    background: active ? c.color : 'transparent',
                    color: active ? '#fff' : 'var(--vrt-navy)',
                    borderColor: active ? c.color : 'var(--vrt-border)',
                  }}
                >
                  {c.label}
                </button>
              );
            })}
          </div>

          <button type="button" className="gestion-btn-reset" onClick={onResetFilters}>
            Réinitialiser
          </button>
        </div>

        {/* Cards grid */}
        {loading ? (
          <div className="gestion-empty">Chargement des capteurs…</div>
        ) : items.length === 0 ? (
          <div className="gestion-empty">Aucun capteur correspondant aux filtres.</div>
        ) : (
          <div className="gestion-grid">
            {items.map((it) => (
              <SensorCard
                key={it.device_id}
                item={it}
                onClick={() => setSelectedDeviceId(it.device_id)}
              />
            ))}
          </div>
        )}
      </main>

      <Footer />

      {selectedItem && (
        <SensorDetailModal
          item={selectedItem}
          onClose={() => setSelectedDeviceId(null)}
          onUpdated={() => fetchAll(filters, true)}
          onDeleted={() => {
            setSelectedDeviceId(null);
            fetchAll(filters, false);
          }}
        />
      )}

      <style>{`
        .gestion-page { min-height: 100vh; background: #fafbfc; }
        .gestion-main {
          max-width: 1400px; margin: 0 auto; padding: 32px 16px 60px;
        }

        /* Header */
        .gestion-header {
          display: flex; justify-content: space-between; align-items: flex-start;
          margin-bottom: 24px; gap: 20px; flex-wrap: wrap;
        }
        .gestion-header-left { flex: 1; min-width: 200px; }
        .gestion-header-right {
          display: flex; align-items: center; gap: 12px;
          flex-wrap: wrap;
        }
        .gestion-title {
          margin: 0 0 6px;
          font-size: 26px; font-weight: 700; color: var(--vrt-navy); line-height: 1.2;
        }
        .gestion-subtitle {
          margin: 0; font-size: 13px; color: var(--vrt-text-muted); line-height: 1.5;
        }
        .gestion-btn-refresh {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 14px;
          border: 1px solid var(--vrt-border); border-radius: 6px;
          background: #fff; color: var(--vrt-navy);
          font-size: 13px; font-weight: 600; cursor: pointer;
          transition: all 0.15s;
        }
        .gestion-btn-refresh:hover { background: var(--vrt-bg-alt); }
        .gestion-pulse-dot {
          display: inline-block;
          width: 10px; height: 10px; border-radius: 50%;
          background: var(--vrt-teal);
          box-shadow: 0 0 0 0 rgba(46, 182, 167, 0.6);
          animation: gestion-pulse 1s ease-out 1;
        }
        @keyframes gestion-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(46, 182, 167, 0.6); transform: scale(1); }
          70%  { box-shadow: 0 0 0 10px rgba(46, 182, 167, 0); transform: scale(1.15); }
          100% { box-shadow: 0 0 0 0 rgba(46, 182, 167, 0); transform: scale(1); }
        }

        /* Error */
        .gestion-error {
          background: #fdecea; color: #a23123;
          padding: 12px 14px; border-radius: 8px;
          margin-bottom: 16px; font-size: 13px;
        }

        /* KPIs */
        .gestion-kpis {
          display: flex; justify-content: space-between; align-items: center;
          flex-wrap: wrap; gap: 16px;
          padding: 20px 0;
          margin-bottom: 24px;
          border-top: 1px solid var(--vrt-border);
          border-bottom: 1px solid var(--vrt-border);
        }
        .gestion-kpi {
          display: flex; flex-direction: column; align-items: center;
          text-align: center; flex: 1; min-width: 90px;
        }
        .gestion-kpi-value {
          font-size: 30px; font-weight: 700; color: var(--vrt-navy);
          line-height: 1; margin-bottom: 4px;
        }
        .gestion-kpi-label {
          font-size: 10px; font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.06em; color: var(--vrt-text-muted); margin-bottom: 2px;
        }
        .gestion-kpi-pct { font-size: 12px; color: var(--vrt-text-muted); }
        .gestion-kpi-separator {
          width: 1px; height: 56px; background: var(--vrt-border); align-self: center;
        }

        /* Filters */
        .gestion-filters {
          display: flex; flex-wrap: wrap; gap: 10px;
          align-items: center;
          padding: 14px;
          background: rgba(227, 231, 238, 0.3);
          border: 1px solid var(--vrt-border);
          border-radius: 8px;
          margin-bottom: 24px;
        }
        .gestion-search {
          position: relative; flex: 1 1 220px; min-width: 200px;
          display: flex; align-items: center;
        }
        .gestion-search svg {
          position: absolute; left: 10px; color: var(--vrt-text-muted); pointer-events: none;
        }
        .gestion-search-input {
          width: 100%;
          padding: 8px 10px 8px 32px;
          border: 1px solid var(--vrt-border); border-radius: 6px;
          font-size: 13px; background: #fff; color: var(--vrt-text);
          font-family: inherit;
        }
        .gestion-search-input:focus { outline: 2px solid var(--vrt-teal); outline-offset: 1px; }
        .gestion-filter-input {
          padding: 8px 10px;
          border: 1px solid var(--vrt-border); border-radius: 6px;
          font-size: 13px; background: #fff; color: var(--vrt-text);
          font-family: inherit; min-width: 160px;
        }
        .gestion-filter-input:focus { outline: 2px solid var(--vrt-teal); outline-offset: 1px; }
        .gestion-chips { display: inline-flex; gap: 6px; flex-wrap: wrap; }
        .gestion-chip {
          padding: 7px 14px;
          border-radius: 999px; border: 1px solid var(--vrt-border);
          font-size: 12px; font-weight: 600;
          cursor: pointer; transition: all 0.15s;
        }
        .gestion-btn-reset {
          padding: 8px 12px;
          border: 1px solid var(--vrt-border); border-radius: 6px;
          background: transparent; color: var(--vrt-navy);
          font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s;
        }
        .gestion-btn-reset:hover { background: var(--vrt-bg-alt); }

        /* Empty */
        .gestion-empty {
          padding: 40px; text-align: center;
          color: var(--vrt-text-muted); background: var(--vrt-bg-alt);
          border-radius: 8px; font-size: 14px;
        }

        /* Grid */
        .gestion-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
          gap: 16px;
        }

        /* Mobile */
        @media (max-width: 720px) {
          .gestion-main { padding: 20px 12px 40px; }
          .gestion-title { font-size: 22px; }
          .gestion-kpis {
            flex-wrap: nowrap;
            overflow-x: auto;
            scroll-snap-type: x mandatory;
            justify-content: flex-start;
            gap: 14px;
            padding: 14px 0;
          }
          .gestion-kpi { scroll-snap-align: start; min-width: 80px; flex: 0 0 auto; }
          .gestion-kpi-separator { display: none; }
          .gestion-grid { grid-template-columns: 1fr; }
          .gestion-filters { flex-direction: column; align-items: stretch; }
          .gestion-search, .gestion-filter-input { flex: 1 1 100%; min-width: unset; }
          .gestion-chips { justify-content: flex-start; }
          .gestion-btn-reset { width: 100%; }
        }
      `}</style>
    </div>
  );
}

/* ---------- Sensor Card ---------- */

function SensorCard({ item, onClick }) {
  const statut = item.statut || 'inconnu';
  const color = STATUT_COLOR[statut];
  const taux = Math.max(0, Math.min(100, item.taux_remplissage ?? 0));
  const isAlert = item.eau_detectee === true;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`sensor-card ${isAlert ? 'sensor-card--alert' : ''}`}
    >
      {isAlert && (
        <div className="sensor-card__alert-banner">
          <AlertTriangle size={14} />
          EAU DÉTECTÉE — INTERVENTION URGENTE
        </div>
      )}

      <div className="sensor-card__head">
        <div className="sensor-card__head-left">
          <div className="sensor-card__id">{item.device_id}</div>
          <div className="sensor-card__sector">{item.secteur || '— Secteur non renseigné'}</div>
        </div>
        <div className="sensor-card__head-right">
          <span
            className="sensor-card__dot"
            style={{ background: item.connecte ? STATUT_COLOR.normal : '#a8b4c9' }}
            title={item.connecte ? 'En ligne' : 'Hors ligne'}
          />
          <span className="sensor-card__time">{timeAgo(item.last_update)}</span>
        </div>
      </div>

      <div className="sensor-card__gauge-row">
        <div className="sensor-card__gauge">
          <div
            className="sensor-card__gauge-fill"
            style={{ width: `${taux}%`, background: color }}
          />
        </div>
        <div className="sensor-card__pct" style={{ color }}>{taux}%</div>
      </div>

      <div className="sensor-card__meta">
        <span>
          Distance :{' '}
          <strong>
            {item.distance_mm === -1
              ? 'eau détectée'
              : item.distance_mm == null
                ? '—'
                : `${item.distance_mm} mm`}
          </strong>
        </span>
        <span
          className="sensor-card__badge"
          style={{ background: STATUT_BG[statut], color }}
        >
          {STATUT_LABEL[statut]}
        </span>
      </div>

      <div className="sensor-card__cta">
        Détail <span aria-hidden>→</span>
      </div>

      <style>{`
        .sensor-card {
          position: relative;
          display: flex; flex-direction: column; gap: 12px;
          background: #fff;
          border: 1px solid var(--vrt-border);
          border-radius: 10px;
          padding: 16px;
          cursor: pointer;
          text-align: left;
          font-family: inherit;
          transition: box-shadow 0.15s, transform 0.15s, border-color 0.15s;
        }
        .sensor-card:hover {
          box-shadow: 0 4px 14px rgba(26, 40, 73, 0.08);
          border-color: var(--vrt-navy-300);
        }
        .sensor-card--alert {
          border: 3px solid #c0392b;
          padding: 14px;
          animation: sensor-alert-pulse 1.5s ease-in-out infinite;
        }
        @keyframes sensor-alert-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(192, 57, 43, 0.35); }
          50%      { box-shadow: 0 0 0 8px rgba(192, 57, 43, 0); }
        }
        .sensor-card__alert-banner {
          display: inline-flex; align-items: center; gap: 6px;
          background: #c0392b; color: #fff;
          padding: 6px 10px; border-radius: 6px;
          font-size: 11px; font-weight: 700;
          letter-spacing: 0.04em;
          margin: -4px -4px 4px;
        }
        .sensor-card__head {
          display: flex; justify-content: space-between; align-items: flex-start; gap: 10px;
        }
        .sensor-card__head-left { min-width: 0; flex: 1; }
        .sensor-card__id {
          font-size: 17px; font-weight: 700; color: var(--vrt-navy);
          font-family: 'Menlo', 'Consolas', monospace;
          word-break: break-all; line-height: 1.2;
        }
        .sensor-card__sector {
          font-size: 12px; color: var(--vrt-text-muted); margin-top: 4px;
        }
        .sensor-card__head-right {
          display: inline-flex; align-items: center; gap: 6px;
          flex-shrink: 0;
        }
        .sensor-card__dot {
          width: 8px; height: 8px; border-radius: 50%;
        }
        .sensor-card__time {
          font-size: 11px; color: var(--vrt-text-muted); white-space: nowrap;
        }
        .sensor-card__gauge-row {
          display: flex; align-items: center; gap: 10px;
        }
        .sensor-card__gauge {
          flex: 1;
          height: 14px;
          background: var(--vrt-bg-alt);
          border-radius: 999px;
          overflow: hidden;
        }
        .sensor-card__gauge-fill {
          height: 100%;
          border-radius: 999px;
          transition: width 0.4s ease;
        }
        .sensor-card__pct {
          font-size: 18px; font-weight: 700;
          min-width: 48px; text-align: right;
        }
        .sensor-card__meta {
          display: flex; justify-content: space-between; align-items: center;
          gap: 8px;
          font-size: 12px; color: var(--vrt-text-muted);
        }
        .sensor-card__meta strong { color: var(--vrt-navy); font-weight: 700; }
        .sensor-card__badge {
          padding: 3px 10px; border-radius: 999px;
          font-size: 11px; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.04em;
        }
        .sensor-card__cta {
          align-self: flex-end;
          font-size: 12px; font-weight: 600;
          color: var(--vrt-teal-700);
        }
      `}</style>
    </button>
  );
}

/* ---------- Detail Modal ---------- */

function SensorDetailModal({ item, onClose, onUpdated, onDeleted }) {
  const [history, setHistory] = useState(null);
  const [historyError, setHistoryError] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const [editing, setEditing] = useState(false);
  const [avaloirs, setAvaloirs] = useState([]);
  const [form, setForm] = useState({
    avaloir_id: item.avaloir_id ?? '',
    latitude: item.latitude ?? '',
    longitude: item.longitude ?? '',
    secteur: item.secteur ?? '',
    notes: item.notes ?? '',
    calibration_empty_mm: item.calibration_empty_mm ?? 700,
    calibration_full_mm: item.calibration_full_mm ?? 150,
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const statut = item.statut || 'inconnu';
  const isAlert = item.eau_detectee === true;

  /* fetch history */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingHistory(true);
      setHistoryError(null);
      try {
        const data = await api.get(`/api/admin/sensors/${encodeURIComponent(item.device_id)}/history?limit=12`);
        if (cancelled) return;
        const arr = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
        // We want oldest → newest for the chart line
        const sorted = [...arr].sort((a, b) => (a.measured_at || 0) - (b.measured_at || 0));
        setHistory(sorted);
      } catch (e) {
        if (!cancelled) setHistoryError(e.message || 'Erreur de chargement de l\'historique');
      } finally {
        if (!cancelled) setLoadingHistory(false);
      }
    })();
    return () => { cancelled = true; };
  }, [item.device_id]);

  /* fetch avaloirs only when entering edit mode */
  useEffect(() => {
    if (!editing || avaloirs.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get('/api/avaloirs?limit=500');
        if (cancelled) return;
        setAvaloirs(Array.isArray(data?.items) ? data.items : []);
      } catch {
        if (!cancelled) setAvaloirs([]);
      }
    })();
    return () => { cancelled = true; };
  }, [editing]);

  /* close on Escape */
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        avaloir_id: form.avaloir_id === '' ? null : form.avaloir_id,
        latitude: form.latitude === '' ? null : Number(form.latitude),
        longitude: form.longitude === '' ? null : Number(form.longitude),
        secteur: form.secteur || null,
        notes: form.notes || null,
        calibration_empty_mm: Number(form.calibration_empty_mm) || 700,
        calibration_full_mm: Number(form.calibration_full_mm) || 150,
      };
      await api.patch(`/api/admin/sensors/${encodeURIComponent(item.device_id)}`, payload);
      setEditing(false);
      onUpdated?.();
    } catch (err) {
      setSaveError(err.message || 'Erreur d\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm(`Supprimer définitivement le capteur ${item.device_id} ?`)) return;
    try {
      await api.del(`/api/admin/sensors/${encodeURIComponent(item.device_id)}`);
      onDeleted?.();
    } catch (e) {
      alert('Suppression impossible : ' + (e.message || 'erreur'));
    }
  };

  const photoUrl = item.avaloir_id ? `/api/avaloirs/${item.avaloir_id}/photo` : null;
  const gmaps =
    item.latitude != null && item.longitude != null
      ? `https://www.google.com/maps?q=${item.latitude},${item.longitude}`
      : null;

  /* chart data */
  const chartData = useMemo(() => {
    if (!history) return [];
    return history.map((h, i) => ({
      idx: i,
      time: fmtTime(h.measured_at),
      taux: h.distance_mm === -1 ? 100 : (h.taux ?? null),
      raw_distance: h.distance_mm,
      is_water: h.distance_mm === -1,
    }));
  }, [history]);

  const waterPoints = chartData.filter((d) => d.is_water);

  return (
    <div className="modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <div>
            <div className="modal__id">{item.device_id}</div>
            <div className="modal__sector">{item.secteur || '— Secteur non renseigné'}</div>
          </div>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Fermer">
            <X size={20} />
          </button>
        </div>

        <div className="modal__body">
          {isAlert && (
            <div className="modal__alert">
              <AlertTriangle size={16} />
              <strong>EAU DÉTECTÉE</strong> — Une intervention est requise.
            </div>
          )}

          {/* Status badge */}
          <div className="modal__status-row">
            <span
              className="modal__status-badge"
              style={{ background: STATUT_BG[statut], color: STATUT_COLOR[statut] }}
            >
              {STATUT_LABEL[statut]}
            </span>
            <span className="modal__status-pct">
              {item.taux_remplissage ?? 0}% rempli ·{' '}
              {item.distance_mm === -1
                ? 'eau détectée'
                : item.distance_mm == null
                  ? 'pas de mesure'
                  : `${item.distance_mm} mm`}
            </span>
          </div>

          {/* Photo if linked */}
          {photoUrl && (
            <div className="modal__photo-wrap">
              <img
                src={photoUrl}
                alt="Avaloir lié"
                className="modal__photo"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>
          )}

          {/* Infos */}
          <div className="modal__infos">
            <InfoRow label="Avaloir lié" value={item.avaloir_id ? `#${item.avaloir_id}` : 'Aucun'} />
            <InfoRow
              label="Coordonnées"
              value={
                item.latitude != null && item.longitude != null ? (
                  <>
                    {item.latitude.toFixed(5)}, {item.longitude.toFixed(5)}
                    {gmaps && (
                      <>
                        {' '}
                        <a href={gmaps} target="_blank" rel="noreferrer" className="modal__link">
                          <ExternalLink size={12} /> Maps
                        </a>
                      </>
                    )}
                  </>
                ) : (
                  '—'
                )
              }
            />
            <InfoRow label="Calibration vide" value={`${item.calibration_empty_mm ?? 700} mm`} />
            <InfoRow label="Calibration plein" value={`${item.calibration_full_mm ?? 150} mm`} />
            <InfoRow label="Dernière mesure" value={timeAgo(item.last_update)} />
            <InfoRow
              label="Connectivité"
              value={item.connecte ? 'En ligne' : 'Hors ligne'}
            />
            {item.notes && <InfoRow label="Notes" value={item.notes} />}
          </div>

          {/* History chart */}
          <div className="modal__section">
            <div className="modal__section-title">Historique des 12 dernières mesures</div>
            {loadingHistory ? (
              <div className="modal__chart-empty">Chargement…</div>
            ) : historyError ? (
              <div className="modal__chart-empty">Erreur : {historyError}</div>
            ) : !chartData.length ? (
              <div className="modal__chart-empty">Aucune mesure disponible.</div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 6 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--vrt-border)" />
                  <XAxis dataKey="time" style={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} style={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{
                      background: '#fff',
                      border: '1px solid var(--vrt-border)',
                      borderRadius: 6,
                      padding: '8px',
                      fontSize: 12,
                    }}
                    formatter={(value, name, props) => {
                      if (props?.payload?.is_water) return ['Eau détectée', 'Mesure'];
                      return [`${value}%`, 'Taux'];
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="taux"
                    stroke="#2EB6A7"
                    strokeWidth={2}
                    dot={{ r: 3, fill: '#2EB6A7' }}
                    activeDot={{ r: 5 }}
                    isAnimationActive={false}
                  />
                  {waterPoints.map((p) => (
                    <ReferenceDot
                      key={p.idx}
                      x={p.time}
                      y={p.taux ?? 100}
                      r={5}
                      fill="#c0392b"
                      stroke="#fff"
                      strokeWidth={1.5}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Edit form */}
          {editing && (
            <form className="modal__form" onSubmit={onSave}>
              <div className="modal__section-title">Modifier le capteur</div>

              <label className="modal__field">
                <span>Lier à un avaloir</span>
                <select
                  className="gestion-filter-input"
                  value={form.avaloir_id ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, avaloir_id: e.target.value }))}
                >
                  <option value="">Aucun avaloir lié</option>
                  {avaloirs.map((a) => (
                    <option key={a.id} value={a.id}>
                      #{a.id}
                      {a.city ? ` — ${a.city}` : ''}
                      {a.latitude != null && a.longitude != null
                        ? ` (${a.latitude.toFixed(4)}, ${a.longitude.toFixed(4)})`
                        : ''}
                    </option>
                  ))}
                </select>
              </label>

              <div className="modal__field-row">
                <label className="modal__field">
                  <span>Latitude</span>
                  <input
                    type="number"
                    step="0.000001"
                    className="gestion-filter-input"
                    value={form.latitude ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, latitude: e.target.value }))}
                  />
                </label>
                <label className="modal__field">
                  <span>Longitude</span>
                  <input
                    type="number"
                    step="0.000001"
                    className="gestion-filter-input"
                    value={form.longitude ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, longitude: e.target.value }))}
                  />
                </label>
              </div>

              <label className="modal__field">
                <span>Secteur</span>
                <input
                  type="text"
                  className="gestion-filter-input"
                  value={form.secteur ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, secteur: e.target.value }))}
                  placeholder="Ex : Centre-ville"
                />
              </label>

              <label className="modal__field">
                <span>Notes</span>
                <textarea
                  className="gestion-filter-input"
                  rows="3"
                  value={form.notes ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Observations, contexte d'installation…"
                  style={{ resize: 'vertical', minHeight: 70 }}
                />
              </label>

              <div className="modal__field-row">
                <label className="modal__field">
                  <span>Calibration vide (mm)</span>
                  <input
                    type="number"
                    className="gestion-filter-input"
                    value={form.calibration_empty_mm ?? 700}
                    onChange={(e) => setForm((f) => ({ ...f, calibration_empty_mm: e.target.value }))}
                  />
                  <small className="modal__hint">700 = vide par défaut</small>
                </label>
                <label className="modal__field">
                  <span>Calibration plein (mm)</span>
                  <input
                    type="number"
                    className="gestion-filter-input"
                    value={form.calibration_full_mm ?? 150}
                    onChange={(e) => setForm((f) => ({ ...f, calibration_full_mm: e.target.value }))}
                  />
                  <small className="modal__hint">150 = plein par défaut</small>
                </label>
              </div>

              {saveError && <div className="gestion-error">{saveError}</div>}

              <div className="modal__form-actions">
                <button type="submit" className="vrt-btn vrt-btn--teal" disabled={saving}>
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
                <button
                  type="button"
                  className="vrt-btn vrt-btn--ghost"
                  onClick={() => { setEditing(false); setSaveError(null); }}
                  disabled={saving}
                >
                  Annuler
                </button>
              </div>
            </form>
          )}
        </div>

        {!editing && (
          <div className="modal__footer">
            <button type="button" className="modal__btn modal__btn--ghost" onClick={() => setEditing(true)}>
              <Edit3 size={14} /> Modifier
            </button>
            <button type="button" className="modal__btn modal__btn--danger" onClick={onDelete}>
              <Trash2 size={14} /> Supprimer
            </button>
          </div>
        )}
      </div>

      <style>{`
        .modal-overlay {
          position: fixed; inset: 0;
          background: rgba(26, 40, 73, 0.5);
          z-index: 1000;
          display: flex; align-items: center; justify-content: center;
          padding: 20px;
          animation: modal-fade-in 0.15s ease-out;
        }
        @keyframes modal-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .modal {
          background: #fff;
          border-radius: 12px;
          width: 100%;
          max-width: 600px;
          max-height: 90vh;
          display: flex; flex-direction: column;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
          overflow: hidden;
        }
        .modal__header {
          display: flex; justify-content: space-between; align-items: flex-start;
          gap: 12px;
          padding: 18px 20px;
          border-bottom: 1px solid var(--vrt-border);
        }
        .modal__id {
          font-size: 18px; font-weight: 700; color: var(--vrt-navy);
          font-family: 'Menlo', 'Consolas', monospace;
          word-break: break-all;
        }
        .modal__sector {
          font-size: 12px; color: var(--vrt-text-muted); margin-top: 4px;
        }
        .modal__close {
          background: transparent; border: 1px solid var(--vrt-border);
          border-radius: 8px;
          width: 36px; height: 36px;
          display: inline-flex; align-items: center; justify-content: center;
          cursor: pointer; color: var(--vrt-text-muted);
          flex-shrink: 0;
        }
        .modal__close:hover { background: var(--vrt-bg-alt); color: var(--vrt-navy); }
        .modal__body {
          flex: 1; overflow-y: auto;
          padding: 18px 20px;
          display: flex; flex-direction: column; gap: 16px;
        }
        .modal__alert {
          display: flex; align-items: center; gap: 10px;
          background: #fdecea; color: #a23123;
          padding: 12px 14px; border-radius: 8px;
          border: 1px solid #f5c6c2;
          font-size: 13px;
        }
        .modal__status-row {
          display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
        }
        .modal__status-badge {
          padding: 4px 12px; border-radius: 999px;
          font-size: 12px; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.04em;
        }
        .modal__status-pct {
          font-size: 13px; color: var(--vrt-navy); font-weight: 600;
        }
        .modal__photo-wrap {
          width: 100%;
          border-radius: 8px;
          overflow: hidden;
          background: var(--vrt-bg-alt);
        }
        .modal__photo {
          width: 100%; height: auto; max-height: 220px;
          object-fit: cover; display: block;
        }
        .modal__infos {
          display: grid; gap: 8px;
          background: var(--vrt-bg-alt);
          border-radius: 8px;
          padding: 12px 14px;
        }
        .modal__link {
          display: inline-flex; align-items: center; gap: 4px;
          color: var(--vrt-teal-700); text-decoration: none;
          font-size: 12px; font-weight: 600;
        }
        .modal__link:hover { text-decoration: underline; }
        .modal__section { display: flex; flex-direction: column; gap: 8px; }
        .modal__section-title {
          font-size: 11px; font-weight: 700;
          color: var(--vrt-navy);
          text-transform: uppercase; letter-spacing: 0.06em;
        }
        .modal__chart-empty {
          padding: 24px; text-align: center;
          color: var(--vrt-text-muted);
          background: var(--vrt-bg-alt);
          border-radius: 8px; font-size: 13px;
        }
        .modal__form {
          display: flex; flex-direction: column; gap: 12px;
          padding-top: 12px;
          border-top: 1px solid var(--vrt-border);
        }
        .modal__field { display: flex; flex-direction: column; gap: 4px; flex: 1; }
        .modal__field > span {
          font-size: 11px; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.05em;
          color: var(--vrt-navy);
        }
        .modal__field-row { display: flex; gap: 12px; flex-wrap: wrap; }
        .modal__field-row .modal__field { min-width: 140px; }
        .modal__hint { font-size: 11px; color: var(--vrt-text-muted); font-style: italic; }
        .modal__form-actions { display: flex; gap: 8px; padding-top: 4px; }
        .modal__form-actions .vrt-btn {
          flex: 1; min-height: 44px; font-size: 14px; padding: 0 14px;
        }
        .modal__footer {
          display: flex; gap: 8px;
          padding: 14px 20px;
          border-top: 1px solid var(--vrt-border);
          background: var(--vrt-bg-alt);
        }
        .modal__btn {
          display: inline-flex; align-items: center; justify-content: center;
          gap: 6px;
          flex: 1; padding: 10px 14px;
          border-radius: 8px;
          font-size: 13px; font-weight: 600;
          cursor: pointer; font-family: inherit;
          transition: all 0.15s;
        }
        .modal__btn--ghost {
          background: #fff;
          border: 1px solid var(--vrt-border);
          color: var(--vrt-navy);
        }
        .modal__btn--ghost:hover {
          background: var(--vrt-bg-alt);
          border-color: var(--vrt-navy-300);
        }
        .modal__btn--danger {
          background: #fff;
          border: 1px solid var(--vrt-danger);
          color: var(--vrt-danger);
        }
        .modal__btn--danger:hover {
          background: var(--vrt-danger);
          color: #fff;
        }

        @media (max-width: 540px) {
          .modal-overlay { padding: 8px; }
          .modal { max-width: 100%; max-height: 95vh; width: 95vw; }
          .modal__header { padding: 14px 16px; }
          .modal__body { padding: 14px 16px; }
          .modal__footer { padding: 12px 16px; }
          .modal__field-row { flex-direction: column; }
        }
      `}</style>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
      <span style={{ color: 'var(--vrt-text-muted)', fontWeight: 600 }}>{label}</span>
      <span style={{ color: 'var(--vrt-navy)', textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
}
